import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import autobind from 'core-decorators/lib/autobind';
import WixSDK from 'shared/utils/wix-sdk';

import { updateDeeplink } from 'widget/utils/widget-info/widget-info';
import { getPublic as getChannel } from 'widget/redux/server/actions/channel/get-public';
import getPaymentInfo from 'widget/redux/server/actions/channel/payment';
import { resetSearch } from 'widget/redux/client/actions/search';
import { resetSelectedVideo } from 'widget/redux/client/actions/select-video';
import { pauseVideo } from 'widget/redux/client/actions/player/change-playback-status';
import { closeModal } from 'widget/components/modal/utils';

import { loadMoreVideoPages } from 'widget/redux/client/lazy-channel-videos/actions';
import {
  getAllSettings,
  getVideoIdFromAppSettings,
  getChannelIdFromAppSettings,
} from 'shared/selectors/app-settings';
import events from 'shared/constants/events';
import { VIEW_MODES } from '@wix/wix-vod-constants/dist/common/view-modes';

const IGNORED_KEYS = ['previewType'];

const getChangedKeys = (target, source) => {
  const keys = _.union(_.keys(target), _.keys(source));
  return _.filter(
    keys,
    key => !_.includes(IGNORED_KEYS, key) && target[key] !== source[key],
  );
};

const getAppSettingsDifference = (props, nextProps) => {
  const {
    booleans: nextBooleans,
    numbers: nextNumbers,
  } = nextProps.appSettings;
  const { booleans: thisBooleans, numbers: thisNumbers } = props.appSettings;

  return {
    booleans: getChangedKeys(nextBooleans, thisBooleans),
    numbers: getChangedKeys(nextNumbers, thisNumbers),
  };
};

const hasChangesInAppSettings = (props, nextProps) => {
  const { booleans, numbers } = getAppSettingsDifference(props, nextProps);
  return booleans.length || numbers.length;
};

@connect(
  state => ({
    currentChannelId: getChannelIdFromAppSettings(state),
    currentVideoId: getVideoIdFromAppSettings(state),
    appSettings: getAllSettings(state),
  }),
  {
    getChannel,
    getPaymentInfo,
    resetSearch,
    resetSelectedVideo,
    pauseVideo,
    fetchVideos: loadMoreVideoPages,
  },
)
export class EditorObserver extends React.Component {
  static propTypes = {
    currentChannelId: PropTypes.string.isRequired,
    currentVideoId: PropTypes.string.isRequired,
    appSettings: PropTypes.object.isRequired,
    fetchVideos: PropTypes.func.isRequired,
    getChannel: PropTypes.func.isRequired,
    getPaymentInfo: PropTypes.func.isRequired,
    resetSearch: PropTypes.func.isRequired,
    resetSelectedVideo: PropTypes.func.isRequired,
    pauseVideo: PropTypes.func.isRequired,
  };

  componentDidMount() {
    this.updateWidgetChannel();

    WixSDK.addEventListener(
      WixSDK.Events.SETTINGS_UPDATED,
      this.reloadOnDashboardClosing,
    );
    WixSDK.addEventListener(WixSDK.Events.EDIT_MODE_CHANGE, this.reset);
    WixSDK.addEventListener(WixSDK.Events.EDIT_MODE_CHANGE, closeModal);
    WixSDK.addEventListener(
      WixSDK.Events.SITE_PUBLISHED,
      this.updateWidgetChannel,
    );
  }

  componentWillReceiveProps(nextProps) {
    if (
      WixSDK.Utils.getViewMode() === VIEW_MODES.EDITOR &&
      hasChangesInAppSettings(this.props, nextProps)
    ) {
      this.reset();
    }
  }

  componentDidUpdate(prevProps) {
    const { currentChannelId, currentVideoId } = this.props;

    if (
      currentChannelId !== prevProps.currentChannelId ||
      currentVideoId !== prevProps.currentVideoId
    ) {
      this.updateWidgetChannel();
    }
  }

  componentWillUnmount() {
    WixSDK.removeEventListener(
      WixSDK.Events.SETTINGS_UPDATED,
      this.reloadOnDashboardClosing,
    );
    WixSDK.removeEventListener(WixSDK.Events.EDIT_MODE_CHANGE, this.reset);
    WixSDK.removeEventListener(WixSDK.Events.EDIT_MODE_CHANGE, closeModal);
    WixSDK.removeEventListener(
      WixSDK.Events.SITE_PUBLISHED,
      this.updateWidgetChannel,
    );
  }

  @autobind
  reloadOnDashboardClosing(event) {
    if (event === events.DASHBOARD.CLOSED) {
      this.reloadData();
      this.reset();
    }
  }

  @autobind
  updateWidgetChannel() {
    const { currentChannelId, currentVideoId } = this.props;

    updateDeeplink(currentChannelId, currentVideoId);
  }

  @autobind
  reset() {
    const { resetSearch, resetSelectedVideo, pauseVideo } = this.props;

    pauseVideo();
    resetSearch();
    resetSelectedVideo();
  }

  reloadData() {
    const { currentChannelId } = this.props;
    const { getChannel, getPaymentInfo, fetchVideos } = this.props;

    getChannel(currentChannelId);
    getPaymentInfo(currentChannelId);
    fetchVideos();
  }

  render() {
    return null;
  }
}
